// Supplier detail page — a single-scroll surface for one supplier driven
// by the enriched rollup from useSupplier (supplier + contacts, rollup,
// contracts, activity feed). Sections, top to bottom:
//   1. Header: back link to the register, name + status, ABN/ACN/since
//      line, and the single primary (brand-violet) Edit action.
//   2. Four StatBlocks: Active contracts, Committed value (ex-GST),
//      Performance (GradeLetter large + the grade roll-up sub-line +
//      grade-help), Next expiry.
//   3. Committed-value stepped chart (the labelled data SVG).
//   4. Contracts table — rows link to the contract detail route.
//   5. Business-details card beside a dense Contacts grid (slate avatars,
//      primary-first, slate Primary tag, capped at 4 with Show all).
//   6. Recent activity feed.
//
// Editing lives here now (not on the register). The Edit action is the
// ONE brand-violet primary on the page; contract-row links and the back
// link are slate with brand text-link hovers (the text-link convention is
// exempt from the violet-density budget); the grade tile uses its own
// tier ramp; the chart uses a single brand stroke (sanctioned data-viz);
// contact avatars and the Primary tag are slate, never violet.

import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { Link, useParams } from "react-router";
import {
  ArrowLeft,
  Pencil,
  Info,
  Building2,
  Mail,
  Phone,
  CalendarClock,
} from "lucide-react";
import {
  Button,
  LoadingSpinner,
  Alert,
  Badge,
  StatBlock,
  Card,
} from "../../components/common";
import { GradeLetter } from "../../components/manage/contract-overview/GradeLetter";
import ActivityLogItem from "../../components/manage/contract-overview/ActivityLogItem";
import SupplierFormModal from "../../components/manage/SupplierFormModal";
// Lazy-loaded so recharts ships as an on-demand chunk rather than in the
// eager bundle — this page is one of only a handful that render a chart.
const SupplierCommittedValueChart = lazy(
  () => import("../../components/manage/SupplierCommittedValueChart"),
);
import { useSupplier } from "../../hooks/useSupplier";
import { useUpdateSupplier } from "../../hooks/useSuppliers";
import { useAuth } from "../../contexts/AuthContext";
import { formatDayMonthYear } from "../../utils/formatShortDate";
import { formatAud0dp } from "../../utils/formatCurrency";
import {
  GRADE_LABELS,
  AWAITING_DATA_LABEL,
  SUPPLIER_GRADE_HELP,
  SUPPLIER_GRADE_LABELS,
  gradeResultForRollup,
} from "../../lib/contract-overview/contractGrade";
import type {
  Supplier,
  Contact,
  SupplierGradeRollup,
  ManagedContractStatus,
  CreateSupplierInput,
  SupplierStatus,
} from "../../types";

const CONTACTS_VISIBLE_CAP = 4;

// Format an ABN as `NN NNN NNN NNN`; pass through non-11-digit legacy data.
function formatAbn(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length !== 11) return raw;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
}


const statusBadgeVariant: Record<
  ManagedContractStatus,
  {
    variant: "draft" | "active" | "expired" | "archived";
    label: string;
  }
> = {
  draft: { variant: "draft", label: "Draft" },
  active: { variant: "active", label: "Active" },
  expired: { variant: "expired", label: "Expired" },
  terminated: { variant: "archived", label: "Terminated" },
  archived: { variant: "archived", label: "Archived" },
};

// First + last initials for the slate avatar. Mirrors AttendeeStack's
// derivation, minus the cosmetic tint rotation (these avatars are slate
// per the supplier-detail brand brief).
function initials(name: string): string {
  const tokens = name.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "?";
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
}

export default function SupplierDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { data, isLoading, error } = useSupplier(id);
  const updateMutation = useUpdateSupplier();

  const [showEdit, setShowEdit] = useState(false);
  const [showGradeHelp, setShowGradeHelp] = useState(false);
  const [showAllContacts, setShowAllContacts] = useState(false);

  // Dismiss the grade-help popover on Escape, matching the register pattern.
  useEffect(() => {
    if (!showGradeHelp) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowGradeHelp(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showGradeHelp]);

  const canEdit =
    user?.role === "admin" ||
    user?.role === "editor" ||
    user?.role === "global-admin";

  const supplier = data?.supplier;

  // Primary-first ordering, computed before any conditional return so the
  // hook order is stable across loading / not-found / loaded states.
  const orderedContacts = useMemo<Contact[]>(() => {
    const contacts = supplier?.contacts ?? [];
    return [...contacts].sort((a, b) => {
      if (a.primary && !b.primary) return -1;
      if (!a.primary && b.primary) return 1;
      return 0;
    });
  }, [supplier?.contacts]);

  if (isLoading) {
    return (
      <div
        className="flex justify-center py-16"
        data-testid="supplier-detail-loading"
      >
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <BackLink />
        <Alert variant="error">{(error as Error).message}</Alert>
      </div>
    );
  }

  if (!data || !supplier) {
    return (
      <div className="space-y-4" data-testid="supplier-detail-not-found">
        <BackLink />
        <Card className="flex flex-col items-center justify-center py-16 text-center">
          <Building2 className="mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
          <p className="text-body text-slate-500 dark:text-slate-400">
            Supplier not found.
          </p>
        </Card>
      </div>
    );
  }

  const { rollup, contracts, activity } = data;

  const handleSubmit = async (
    formData: CreateSupplierInput & { status?: SupplierStatus },
  ) => {
    await updateMutation.mutateAsync({ id, input: formData });
    setShowEdit(false);
  };

  const visibleContacts =
    showAllContacts || orderedContacts.length <= CONTACTS_VISIBLE_CAP
      ? orderedContacts
      : orderedContacts.slice(0, CONTACTS_VISIBLE_CAP);

  return (
    <div className="space-y-6">
      <BackLink />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-h1 text-slate-900 dark:text-slate-50">
              {supplier.name}
            </h1>
            {supplier.status === "active" ? (
              <Badge variant="active" label="Active" />
            ) : (
              <Badge variant="archived" label="Inactive" />
            )}
          </div>
          <p className="mt-1 text-body-sm text-slate-500 dark:text-slate-400">
            <span className="font-mono">ABN {formatAbn(supplier.abn)}</span>
            {supplier.acn && (
              <>
                {" · "}
                <span className="font-mono">ACN {supplier.acn}</span>
              </>
            )}
            {rollup.since && (
              <> {" · "}Supplier since {formatDayMonthYear(rollup.since)}</>
            )}
          </p>
        </div>
        {canEdit && (
          <Button
            icon={<Pencil className="h-4 w-4" />}
            onClick={() => setShowEdit(true)}
          >
            Edit
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatBlock
          label="Active contracts"
          value={rollup.activeContracts ?? "—"}
          sub={
            rollup.totalContracts != null
              ? `${rollup.totalContracts} total`
              : "rollup unavailable"
          }
        />
        <StatBlock
          label="Committed value"
          value={
            rollup.committedValue != null
              ? formatAud0dp(rollup.committedValue)
              : "—"
          }
          sub="ex-GST, active contracts"
        />
        <PerformanceStat
          rollup={rollup.gradeRollup}
          showHelp={showGradeHelp}
          onToggleHelp={() => setShowGradeHelp((v) => !v)}
        />
        <NextExpiryStat
          date={rollup.nextExpiry?.date ?? null}
          contractTitle={rollup.nextExpiry?.contractTitle ?? null}
        />
      </div>

      <Card padding="md" className="space-y-3">
        <div>
          <h2 className="text-h4 text-slate-900 dark:text-slate-50">
            Committed value by quarter
          </h2>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            As-of each quarter-end — the committed value of contracts active at
            that time.
          </p>
        </div>
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          }
        >
          <SupplierCommittedValueChart series={rollup.committedValueByQuarter} />
        </Suspense>
      </Card>

      <ContractsTable contracts={contracts} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BusinessDetailsCard supplier={supplier} since={rollup.since} />
        <ContactsCard
          contacts={visibleContacts}
          total={orderedContacts.length}
          showingAll={
            showAllContacts || orderedContacts.length <= CONTACTS_VISIBLE_CAP
          }
          onToggleShowAll={() => setShowAllContacts((v) => !v)}
        />
      </div>

      <ActivityFeed
        entries={activity.entries}
        degraded={activity.degraded}
        contractsConsidered={activity.contractsConsidered}
        contractsTotal={activity.contractsTotal}
      />

      <SupplierFormModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        onSubmit={handleSubmit}
        supplier={supplier}
        isLoading={updateMutation.isPending}
      />
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/manage/suppliers"
      className="inline-flex items-center gap-1.5 text-body-sm text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Supplier Register
    </Link>
  );
}

// Performance stat — a compact grade badge paired with the supplier grade word
// (Strong/Good/Fair/Poor/Critical) over the grade roll-up sub-line, with a
// click-toggle help popover. Rendered as a Card to match StatBlock chrome.
function PerformanceStat({
  rollup,
  showHelp,
  onToggleHelp,
}: {
  rollup: SupplierGradeRollup | null;
  showHelp: boolean;
  onToggleHelp: () => void;
}) {
  return (
    <Card padding="md">
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center justify-between gap-2">
          <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
            Performance
          </p>
          <span className="relative inline-flex">
            <button
              type="button"
              onClick={onToggleHelp}
              aria-label="How the grade is computed"
              aria-expanded={showHelp}
              aria-controls="supplier-grade-help-panel"
              aria-describedby={showHelp ? "supplier-grade-help-panel" : undefined}
              className="inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <Info className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            {showHelp && (
              <div
                id="supplier-grade-help-panel"
                role="region"
                aria-label="Grade computation explanation"
                className="absolute right-0 top-6 z-10 w-64 rounded-md border border-slate-200 bg-white p-3 text-left text-caption font-normal normal-case tracking-normal text-slate-600 shadow-soft-lg dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:shadow-soft-lg-dark"
              >
                {SUPPLIER_GRADE_HELP}
              </div>
            )}
          </span>
        </div>
        <div
          className="flex items-center gap-2.5"
          data-testid="supplier-performance-grade"
        >
          {rollup?.letter ? (
            <>
              <GradeLetter
                result={gradeResultForRollup(rollup)}
                variant="compact"
                label={SUPPLIER_GRADE_LABELS[rollup.letter]}
              />
              <span className="text-h3 text-slate-900 dark:text-slate-50">
                {SUPPLIER_GRADE_LABELS[rollup.letter]}
              </span>
            </>
          ) : (
            <span
              className="text-h3 text-slate-400 dark:text-slate-500"
              aria-label="Not yet graded"
            >
              —
            </span>
          )}
        </div>
        <p className="text-caption text-slate-500 dark:text-slate-400">
          {rollup?.sub ?? "No graded active contracts."}
        </p>
      </div>
    </Card>
  );
}

function NextExpiryStat({
  date,
  contractTitle,
}: {
  date: string | null;
  contractTitle: string | null;
}) {
  if (!date) {
    return (
      <Card padding="md">
        <div className="flex flex-col gap-2.5">
          <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
            Next expiry
          </p>
          <div className="flex items-center gap-2 text-slate-400 dark:text-slate-500">
            <CalendarClock className="h-5 w-5" aria-hidden="true" />
            <span className="text-body-sm">No upcoming expiry</span>
          </div>
          <span className="text-caption text-slate-500 dark:text-slate-400">
            no active contract with an end date
          </span>
        </div>
      </Card>
    );
  }
  return (
    <StatBlock
      label="Next expiry"
      value={formatDayMonthYear(date)}
      sub={contractTitle ?? "active contract"}
    />
  );
}

function ContractsTable({
  contracts,
}: {
  contracts: import("../../types").EnrichedContract[];
}) {
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
        <h2 className="text-h4 text-slate-900 dark:text-slate-50">Contracts</h2>
      </div>
      {contracts.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 text-center"
          data-testid="supplier-contracts-empty"
        >
          <Building2 className="mb-3 h-10 w-10 text-slate-300 dark:text-slate-600" />
          <p className="text-body-sm text-slate-500 dark:text-slate-400">
            No contracts recorded for this supplier yet.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-left text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Number
                </th>
                <th className="px-4 py-3 text-left text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Status
                </th>
                <th className="px-4 py-3 text-center text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Grade
                </th>
                <th className="px-4 py-3 text-left text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  End date
                </th>
                <th className="px-4 py-3 text-right text-caption font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Value
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {contracts.map((contract) => {
                const badge = statusBadgeVariant[contract.status];
                return (
                  <tr
                    key={contract.id}
                    data-testid="supplier-contract-row"
                    className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-3 font-mono text-body-sm text-slate-600 dark:text-slate-400">
                      {contract.contractNumber}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/manage/contracts/${contract.id}`}
                        className="text-body font-medium text-slate-900 hover:text-brand-600 dark:text-slate-50 dark:hover:text-brand-400"
                      >
                        {contract.title}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={badge.variant} label={badge.label} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <GradeLetter
                        result={{
                          grade: contract.grade,
                          score: null,
                          label: contract.grade
                            ? GRADE_LABELS[contract.grade]
                            : AWAITING_DATA_LABEL,
                          reportingPillarCount: 0,
                        }}
                        variant="compact"
                      />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-body-sm tabular-nums text-slate-600 dark:text-slate-400">
                      {formatDayMonthYear(contract.endDate)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-body-sm tabular-nums text-slate-700 dark:text-slate-300">
                      {formatAud0dp(contract.currentValue)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function BusinessDetailsCard({
  supplier,
  since,
}: {
  supplier: Supplier;
  since: string | null;
}) {
  const rows: { label: string; value: React.ReactNode }[] = [
    { label: "ABN", value: <span className="font-mono">{formatAbn(supplier.abn)}</span> },
  ];
  if (supplier.acn) {
    rows.push({
      label: "ACN",
      value: <span className="font-mono">{supplier.acn}</span>,
    });
  }
  if (supplier.address) {
    rows.push({ label: "Address", value: supplier.address });
  }
  if (since) {
    rows.push({ label: "Supplier since", value: formatDayMonthYear(since) });
  }

  return (
    <Card padding="md" className="space-y-3 lg:col-span-1">
      <h2 className="text-h4 text-slate-900 dark:text-slate-50">
        Business details
      </h2>
      <dl className="space-y-2">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col gap-0.5">
            <dt className="text-overline uppercase text-slate-500 dark:text-slate-400">
              {row.label}
            </dt>
            <dd className="text-body-sm text-slate-700 dark:text-slate-300">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      {supplier.notes && (
        <div className="border-t border-slate-100 pt-3 dark:border-slate-700">
          <p className="text-overline uppercase text-slate-500 dark:text-slate-400">
            Notes
          </p>
          <p className="mt-0.5 whitespace-pre-wrap text-body-sm text-slate-700 dark:text-slate-300">
            {supplier.notes}
          </p>
        </div>
      )}
    </Card>
  );
}

function ContactsCard({
  contacts,
  total,
  showingAll,
  onToggleShowAll,
}: {
  contacts: Contact[];
  total: number;
  showingAll: boolean;
  onToggleShowAll: () => void;
}) {
  return (
    <Card padding="md" className="space-y-3 lg:col-span-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-h4 text-slate-900 dark:text-slate-50">Contacts</h2>
        {total > CONTACTS_VISIBLE_CAP && (
          <button
            type="button"
            onClick={onToggleShowAll}
            className="text-caption font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
          >
            {showingAll ? "Show fewer" : `Show all ${total}`}
          </button>
        )}
      </div>
      {total === 0 ? (
        <p className="text-body-sm italic text-slate-500 dark:text-slate-400">
          No contacts recorded.
        </p>
      ) : (
        <div
          data-testid="supplier-contacts-grid"
          className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {contacts.map((contact, i) => (
            <ContactTile key={`${contact.email ?? contact.name ?? i}`} contact={contact} />
          ))}
        </div>
      )}
    </Card>
  );
}

function ContactTile({ contact }: { contact: Contact }) {
  const name = contact.name?.trim() || "Unnamed contact";
  return (
    <div
      data-testid="supplier-contact-tile"
      className="flex gap-3 rounded-lg border border-slate-200/60 bg-slate-50/60 p-3 dark:border-slate-700 dark:bg-slate-800/40"
    >
      <div
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-caption font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300"
      >
        {initials(name)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-body-sm font-medium text-slate-900 dark:text-slate-50">
            {name}
          </span>
          {contact.primary && (
            <span className="inline-flex shrink-0 items-center rounded-full bg-slate-200 px-2 py-0.5 text-overline uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
              Primary
            </span>
          )}
        </div>
        {contact.role && (
          <p className="truncate text-caption text-slate-500 dark:text-slate-400">
            {contact.role}
          </p>
        )}
        <div className="mt-1 space-y-0.5">
          {contact.email && (
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-1.5 text-caption text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
            >
              <Mail className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{contact.email}</span>
            </a>
          )}
          {contact.phone && (
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-1.5 text-caption text-slate-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-brand-400"
            >
              <Phone className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{contact.phone}</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityFeed({
  entries,
  degraded,
  contractsConsidered,
  contractsTotal,
}: {
  entries: import("../../types").ActivityEntry[];
  degraded: boolean;
  contractsConsidered: number;
  contractsTotal: number;
}) {
  return (
    <Card padding="md">
      <div className="mb-3">
        <h2 className="text-h4 text-slate-900 dark:text-slate-50">
          Recent activity
        </h2>
        {degraded && (
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Showing activity from {contractsConsidered} of {contractsTotal}{" "}
            contracts.
          </p>
        )}
      </div>
      {entries.length === 0 ? (
        <p
          data-testid="supplier-activity-empty"
          className="text-body-sm italic text-slate-500 dark:text-slate-400"
        >
          No activity recorded yet — events will appear as this supplier&apos;s
          contracts are updated.
        </p>
      ) : (
        <ol role="list" className="space-y-1">
          {entries.map((entry, i) => (
            <li key={`${entry.occurredAt}#${i}`}>
              <ActivityLogItem entry={entry} />
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
